import { mirrorLocale } from "./prefs.js";

/**
 * Which language the interface is in.
 *
 * Shaped exactly like the theme choice next door, and for the same reason:
 * **three states, not two.** "System" must store nothing, so that a person who
 * never chose follows their browser and keeps following it if they change it.
 * Storing `"ru"` for them would look identical right up until the day it did
 * not.
 *
 * Kept in `localStorage` rather than on the server: the language somebody reads
 * the app in belongs to the device they are holding, not to the account. The
 * phone in Russian and the laptop in English is a real thing people do, and a
 * server-side setting would fight it.
 */

export const LOCALES = ["ru", "en"] as const;

export type Locale = (typeof LOCALES)[number];

/** What the person picked. `system` means "whatever the browser says". */
export type LocaleChoice = Locale | "system";

/**
 * A language names itself.
 *
 * «Русский» and "English" are written in the language they name, not translated
 * into the one currently on screen — that is what every language picker does,
 * and it is what lets someone who cannot read the current interface find their
 * way out of it. The "follow the system" option is a sentence rather than a
 * name, so it lives in the settings messages instead.
 */
export const LOCALE_LABEL: Record<Locale, string> = {
  ru: "Русский",
  en: "English",
};

const KEY = "vmeste.locale";

/** English is the fallback for a browser set to neither of our two languages. */
const FALLBACK: Locale = "en";

function isLocale(value: unknown): value is Locale {
  return LOCALES.includes(value as Locale);
}

/**
 * Read the browser's preference.
 *
 * Walks `languages` in order rather than looking only at `language`, so someone
 * whose first choice is a language we do not have but whose second is Russian
 * gets Russian instead of the fallback. Matching is on the primary subtag, so
 * `ru-BY` and `en-GB` count.
 */
export function detectLocale(languages: readonly string[]): Locale {
  for (const tag of languages) {
    const primary = tag.toLowerCase().split("-")[0];
    if (isLocale(primary)) return primary;
  }
  return FALLBACK;
}

function browserLanguages(): readonly string[] {
  if (typeof navigator === "undefined") return [];
  return navigator.languages?.length ? navigator.languages : [navigator.language].filter(Boolean);
}

/** Turn a choice into the language actually rendered. */
export function resolveLocale(choice: LocaleChoice): Locale {
  return choice === "system" ? detectLocale(browserLanguages()) : choice;
}

/** What is stored, or `system` when nothing valid is. */
export function readStoredChoice(): LocaleChoice {
  try {
    const stored = localStorage.getItem(KEY);
    return isLocale(stored) ? stored : "system";
  } catch {
    // Private browsing, or site data turned off. Not worth a crash on boot.
    return "system";
  }
}

/**
 * Put the language on the document.
 *
 * `lang` is not cosmetic: it is what hyphenation, spell-check and a screen
 * reader's pronunciation all read, and getting it wrong makes a Russian page
 * read aloud in an English voice.
 */
export function applyLocale(locale: Locale): void {
  if (typeof document !== "undefined") {
    document.documentElement.lang = locale;
  }
  mirrorLocale(locale);
}

/* ------------------------------------------------------------------- store */

let choice: LocaleChoice = readStoredChoice();
let locale: Locale = resolveLocale(choice);

const listeners = new Set<() => void>();

export function getLocaleChoice(): LocaleChoice {
  return choice;
}

export function getLocale(): Locale {
  return locale;
}

export function subscribeToLocale(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function setLocaleChoice(next: LocaleChoice): void {
  choice = next;
  locale = resolveLocale(next);

  try {
    // "System" forgets rather than storing a third value, so that a device that
    // later changes language is followed instead of overruled.
    if (next === "system") localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, next);
  } catch {
    // The choice still applies for this session; it just will not survive.
  }

  applyLocale(locale);
  for (const listener of listeners) listener();
}

/** Re-read storage and the browser. Only the tests and the boot path need this. */
export function refreshLocale(): void {
  choice = readStoredChoice();
  locale = resolveLocale(choice);
  applyLocale(locale);
  for (const listener of listeners) listener();
}
